import { defineComponent as _defineComponent } from 'vue'
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, onMounted, ref } from 'vue';
import { formatRuntime } from '@/helpers/title-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { ModuleContentTitleTrend } from '@/@types/graphql-types';
import { useRoute } from 'vue-router/composables';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { numberToCompactString } from '@/helpers/NumberFormat';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import TextRecommendationModal from '@/features/why-to-watch/components/TextRecommendationModal.vue';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { faCaretUp, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// SEASON_PAGE_BUYBOX_EXP
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { AccountModes } from '@/enums/login';
// SEASON_PAGE_BUYBOX_EXP

import PromotionsConfig from '@/features/promotions/config.json';
import { useWtwTracking } from '@/features/why-to-watch/tracking';


export default /*#__PURE__*/_defineComponent({
  __name: 'titleDetailInfoRow',
  props: {
    title: null,
    showChartInfo: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
	title: TitleDetail;
	showChartInfo: boolean;
};

const { t } = useI18n();



const { isAdmin, isEditor, profileIsComplete, profileInfo, isLoggedIn } = useUserStore();
const { country } = useLanguageStore();

const route = useRoute();

const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
});

const displayTextRecommendationModal = ref<boolean>(false);

const textRecommendationUserCountries = PromotionsConfig.w2w.pro_users_countries;

const isEmpty = computed(() => {
	return !streamingChartRank.value && !jwScore.value && !imdbScore.value && !ageRating.value && !runtime.value;
});

const streamingChartInfo = computed<any>(() => {
	return props.title.streamingCharts?.edges?.[0].streamingChartInfo;
});

const streamingChartRank = computed(() => {
	return props.showChartInfo && streamingChartInfo.value?.rank
		? `${streamingChartInfo.value?.rank.toString().padStart(2, '0')}.`
		: null;
});

const ageRating = computed(() => {
	return props.title.__typename === 'Season'
		? props.title.show.content.ageCertification
		: props.title.content.ageCertification;
});

const runtime = computed(() => {
	if (props.title?.content?.runtime) {
		const runtimeValue = props.title.content.runtime!;
		const { translationKey, minutes, hours } = formatRuntime(runtimeValue);
		return t(translationKey, { minutes, hours }) as string;
	}
	return null;
});

const imdbScore = computed(() => {
	return props.title.content.scoring.imdbScore?.toFixed(1);
});

const imdbVoteCount = computed(() => {
	return props.title.content.scoring.imdbVotes;
});

// eslint-disable-next-line vue/return-in-computed-property
const trendDifference = computed(() => {
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Up) {
		return `+${streamingChartInfo.value?.trendDifference}`;
	}
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Stable) {
		return '—';
	}
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Down) {
		return `${streamingChartInfo.value?.trendDifference}`;
	}
});

const showArrow = computed(() => {
	return streamingChartInfo.value?.trend;
});

const arrowClasses = computed(() => {
	if (!showArrow.value) {
		return null;
	}
	return {
		'arrow-container__arrow': !!streamingChartInfo.value?.trend,
		'arrow-container__arrow--up': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Up,
		'arrow-container__arrow--stable': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Stable,
		'arrow-container__arrow--down': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Down,
	};
});

const jwScore = computed(() => {
	return Math.round((props.title.content.scoring?.jwRating || 0) * 100);
});

const showTextRecommendationTools = computed(() => {
	return false;
	if (!isLoggedIn.value) return false;

	if (isAdmin.value || isEditor.value) {
		return true;
	} else if (!textRecommendationUserCountries.includes(country.value)) {
		return false;
	}

	const profileCountry = profileInfo.value?.country;
	return profileIsComplete.value ? isLoggedIn.value && profileCountry === country.value : isLoggedIn.value;
});

const { trackWtw } = useWtwTracking({ title: () => props.title });

function openTextRecommendationModal() {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	trackWtw('modalOpened');

	displayTextRecommendationModal.value = true;
	disableScroll();
}

function closeTextRecommendationModal() {
	displayTextRecommendationModal.value = false;
	enableScroll();
}

function onRatingClick($event: Event) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'rating_click',
			label: 'IMDB',
		},
		[]
	);

	const imdbId = props.title.content.externalIds.imdbId;

	// Reconstruct IMDb link to use current IMDb website URL formatting
	if (route.name === 'app.detail.show.season') {
		let idInPath = imdbId;
		if (imdbId?.includes(':')) {
			const [id] = imdbId.split(':', 2);
			idInPath = id;
		}
		window.open(`https://www.imdb.com/title/${idInPath}?ref_=ref_ext_justwatch`);
	} else {
		window.open(`https://www.imdb.com/title/${imdbId}?ref_=ref_ext_justwatch`);
	}
}

function formatVotes(votes: number | null | undefined) {
	if (!votes || votes < 30) return '';
	if (votes >= 10000) return `(${numberToCompactString(votes, 0)})`;
	return `(${numberToCompactString(votes, 1)})`;
}

return { __sfc: true,t, props, isAdmin, isEditor, profileIsComplete, profileInfo, isLoggedIn, country, route, isMounted, displayTextRecommendationModal, textRecommendationUserCountries, isEmpty, streamingChartInfo, streamingChartRank, ageRating, runtime, imdbScore, imdbVoteCount, trendDifference, showArrow, arrowClasses, jwScore, showTextRecommendationTools, trackWtw, openTextRecommendationModal, closeTextRecommendationModal, onRatingClick, formatVotes, HiddenHorizontalScrollbar, TextRecommendationModal, faCaretUp, faInfoCircle }
}

})